import React, { useEffect, useState } from 'react';
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import DataTableBody from './DataTableBody';
import DataTableHeader from './DataTableHeader';
import DataTablePagination from './DataTablePagination';
import DataTableToolbar from './DataTableToolbar';

const getDateOnlyValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const stringValue = String(value ?? '');
  if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) return stringValue.slice(0, 10);

  const parsed = new Date(stringValue);
  if (Number.isNaN(parsed.getTime())) return '';
  return getDateOnlyValue(parsed);
};

const tableFilterFns = {
  multiSelect: (row, columnId, selectedValues) => (
    Array.isArray(selectedValues)
    && selectedValues.some(
      (selectedValue) => String(selectedValue).toLowerCase()
        === String(row.getValue(columnId) ?? '').toLowerCase(),
    )
  ),
  numberEquals: (row, columnId, filterValue) => {
    const rowValue = row.getValue(columnId);
    if (rowValue === null || rowValue === undefined || rowValue === '') return false;
    const value = Number(rowValue);
    const target = Number(filterValue);
    return Number.isFinite(value) && Number.isFinite(target) && value === target;
  },
  dateEquals: (row, columnId, filterValue) => (
    getDateOnlyValue(row.getValue(columnId)) === String(filterValue)
  ),
  dateRange: (row, columnId, filterValue) => {
    if (!Array.isArray(filterValue)) return true;
    const value = getDateOnlyValue(row.getValue(columnId));
    if (!value) return false;
    const [minimum, maximum] = filterValue;
    return (!minimum || value >= minimum) && (!maximum || value <= maximum);
  },
  booleanEquals: (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (typeof value === 'boolean') return value === filterValue;
    if (value === 'true' || value === 1) return filterValue === true;
    if (value === 'false' || value === 0) return filterValue === false;
    return false;
  },
};

const filterFnByVariant = {
  text: 'includesString',
  select: 'equalsString',
  'multi-select': 'multiSelect',
  number: 'numberEquals',
  'number-range': 'inNumberRange',
  date: 'dateEquals',
  'date-range': 'dateRange',
  boolean: 'booleanEquals',
};

const withFilterFunctions = (columns) => columns.map((column) => {
  if (column.columns) {
    return { ...column, columns: withFilterFunctions(column.columns) };
  }
  const variant = column.meta?.filter?.variant;
  const filterFn = column.filterFn || filterFnByVariant[variant];
  return filterFn ? { ...column, filterFn } : column;
});

const resolveUpdater = (updater, current) => (
  typeof updater === 'function' ? updater(current) : updater
);

/* eslint-disable react/prop-types */
/**
 * @template {import('@tanstack/react-table').RowData} TData
 * @template TValue
 * @param {import('./types').DataTableProps<TData, TValue>} props
 */
const DataTable = ({
  columns,
  data,
  loading = false,
  error,
  emptyMessage = 'No records found',
  enableSorting = true,
  enableMultiSort = true,
  enableGlobalFilter = false,
  enableColumnFilters = false,
  enableColumnVisibility = true,
  enableRowSelection = false,
  enableColumnResizing = false,
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  rowCount,
  pageCount,
  getRowId,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  pagination: controlledPagination,
  onPaginationChange,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  onSelectedRowsChange,
  initialPageSize = 10,
  searchPlaceholder = 'Search...',
  toolbarStart,
  toolbarActions,
  clearGlobalFilterOnClear = true,
  className = '',
  maxBodyHeight,
  headerClassName,
  onRowClick,
}) => {
  const [sortingState, setSortingState] = useState([]);
  const [columnFiltersState, setColumnFiltersState] = useState([]);
  const [globalFilterState, setGlobalFilterState] = useState('');
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [columnVisibilityState, setColumnVisibilityState] = useState({});
  const [rowSelectionState, setRowSelectionState] = useState({});

  const sorting = controlledSorting ?? sortingState;
  const columnFilters = controlledColumnFilters ?? columnFiltersState;
  const globalFilter = controlledGlobalFilter ?? globalFilterState;
  const pagination = controlledPagination ?? paginationState;
  const columnVisibility = controlledColumnVisibility ?? columnVisibilityState;
  const rowSelection = controlledRowSelection ?? rowSelectionState;

  const updateState = (current, setInternalState, onChange, controlled, updater) => {
    const next = resolveUpdater(updater, current);
    if (controlled === undefined) setInternalState(next);
    onChange?.(updater);
    return next;
  };

  const resolvedColumns = withFilterFunctions(columns);
  const tableColumns = enableRowSelection
    ? [
        {
          id: '__selection',
          enableSorting: false,
          enableHiding: false,
          size: 44,
          minSize: 44,
          maxSize: 44,
          header: ({ table }) => (
            <input
              type="checkbox"
              checked={table.getIsAllPageRowsSelected()}
              ref={(element) => {
                if (element) element.indeterminate = table.getIsSomePageRowsSelected();
              }}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              disabled={!table.getRowModel().rows.length}
              aria-label="Select all rows on this page"
              className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
            />
          ),
          cell: ({ row }) => (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
              aria-label={`Select row ${row.index + 1}`}
              className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
            />
          ),
        },
        ...resolvedColumns,
      ]
    : resolvedColumns;

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    enableSorting,
    enableMultiSort,
    enableGlobalFilter,
    enableColumnFilters,
    enableRowSelection,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: 100 },
    manualPagination,
    manualSorting,
    manualFiltering,
    filterFns: tableFilterFns,
    pageCount,
    rowCount,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: (updater) =>
      updateState(sorting, setSortingState, onSortingChange, controlledSorting, updater),
    onColumnFiltersChange: (updater) => {
      const nextFilters = updateState(
        columnFilters,
        setColumnFiltersState,
        onColumnFiltersChange,
        controlledColumnFilters,
        updater,
      );
      if (JSON.stringify(nextFilters) !== JSON.stringify(columnFilters)) {
        table.setPageIndex(0);
      }
    },
    onGlobalFilterChange: (updater) => {
      const nextGlobalFilter = updateState(
        globalFilter,
        setGlobalFilterState,
        onGlobalFilterChange,
        controlledGlobalFilter,
        updater,
      );
      if (nextGlobalFilter !== globalFilter) table.setPageIndex(0);
    },
    onPaginationChange: (updater) =>
      updateState(pagination, setPaginationState, onPaginationChange, controlledPagination, updater),
    onColumnVisibilityChange: (updater) =>
      updateState(columnVisibility, setColumnVisibilityState, onColumnVisibilityChange, controlledColumnVisibility, updater),
    onRowSelectionChange: (updater) =>
      updateState(rowSelection, setRowSelectionState, onRowSelectionChange, controlledRowSelection, updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  useEffect(() => {
    onSelectedRowsChange?.(table.getSelectedRowModel().rows.map((row) => row.original));
  }, [data, onSelectedRowsChange, rowSelection, table]);

  return (
    <div className={className}>
      <DataTableToolbar
        table={table}
        enableGlobalFilter={enableGlobalFilter}
        enableColumnVisibility={enableColumnVisibility}
        globalFilter={String(globalFilter ?? '')}
        onGlobalFilterChange={(value) => table.setGlobalFilter(value)}
        searchPlaceholder={searchPlaceholder}
        startContent={toolbarStart}
        actions={toolbarActions}
        clearGlobalFilterOnClear={clearGlobalFilterOnClear}
      />
      <div
        className={`rounded-lg border border-gray-200 bg-white ${maxBodyHeight ? 'overflow-auto' : 'overflow-x-auto'}`}
        style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
      >
        <table
          className="w-full border-collapse text-left text-sm"
          style={{
            width: `${Math.max(table.getTotalSize(), 1)}px`,
            minWidth: '100%',
            tableLayout: 'fixed',
          }}
          aria-busy={loading}
        >
          <colgroup>
            {table.getVisibleLeafColumns().map((column) => (
              <col key={column.id} style={{ width: `${column.getSize()}px` }} />
            ))}
          </colgroup>
          <DataTableHeader
            table={table}
            enableColumnResizing={enableColumnResizing}
            enableColumnFilters={enableColumnFilters}
            stickyHeader={Boolean(maxBodyHeight)}
            headerClassName={headerClassName}
          />
          <DataTableBody
            table={table}
            loading={loading}
            onRowClick={onRowClick}
            error={error}
            emptyMessage={emptyMessage}
          />
        </table>
      </div>
      <DataTablePagination table={table} loading={loading} />
    </div>
  );
};

export default DataTable;
